import React, { useEffect, useLayoutEffect } from 'react';

const MOBILE_BREAKPOINT = 768;

// useLayoutEffect warns when invoked during SSR; swap in useEffect on the
// server (it never runs there anyway — this only silences the warning).
const useIsomorphicLayoutEffect =
  typeof window === 'undefined' ? useEffect : useLayoutEffect;

export const useIsMobile = () => {
  // HYDRATION CONTRACT (#418 fix, 2026-07-06): the FIRST client render must
  // match the server render, so state starts `undefined` (server markup =
  // desktop branch) on BOTH sides. The previous version initialized
  // synchronously from matchMedia, which made phones hydrate a DIFFERENT
  // component tree than the SSR HTML (YtAnalysisDesktop vs YtAnalysisMobile
  // on /videos/youtube/[slug] hard loads) — React 19 throws recoverable
  // error #418 and regenerates the whole tree on the client for every such
  // load. That was the dominant identified class in the production
  // $exception floor.
  //
  // The flip to the real value happens in useLayoutEffect: it runs after
  // hydration commits but BEFORE the browser paints, so the desktop-modal
  // flash that motivated the old synchronous init still never becomes
  // visible. (The one-paint flash existed because the original code used
  // useEffect, which runs after paint.) Cost on phones: the desktop subtree
  // mounts and is immediately replaced pre-paint — invisible, and strictly
  // cheaper than React 19 discarding and re-rendering the entire root on a
  // hydration mismatch.
  const [isMobile, setIsMobile] = React.useState<boolean | undefined>(
    undefined,
  );

  useIsomorphicLayoutEffect(() => {
    const mql = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT - 1}px)`);

    const onChange = () => {
      setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
    };
    mql.addEventListener('change', onChange);

    setIsMobile(mql.matches);

    return () => mql.removeEventListener('change', onChange);
  }, []);

  return !!isMobile;
};
